<script setup>
import { computed, onMounted, ref, watch } from 'vue'
import { useRoute } from 'vue-router'
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog'
import { Button } from '@/components/ui/button'
import PricingCards from '@/components/PricingCards.vue'
import trpc from '@/trpc'

const props = defineProps({
  session: {
    type: Object,
    default: null,
  },
})

const emit = defineEmits(['close'])

const route = useRoute()

const DISMISSAL_KEY = 'planSelectionModalDismissed'
const PERMANENT_DISMISSAL_KEY = 'planSelectionModalPermanentlyDismissed'
const DISMISSAL_DURATION = 2 * 24 * 60 * 60 * 1000 // 2 days in milliseconds

const isOpen = ref(false)
const subscriptionData = ref(null)
const isLoadingSubscription = ref(false)

// Check subscription status from API
const hasActivePlan = computed(() => {
  return subscriptionData.value?.isSubscribed || false
})

// Check if modal was permanently dismissed (after successful purchase)
const isPermanentlyDismissed = computed(() => {
  return localStorage.getItem(PERMANENT_DISMISSAL_KEY) === 'true'
})

// Check if modal was dismissed recently (temporary dismissal)
const isDismissed = computed(() => {
  const dismissedAt = localStorage.getItem(DISMISSAL_KEY)
  if (!dismissedAt) {
    return false
  }

  const dismissedTime = Number.parseInt(dismissedAt)
  const now = Date.now()

  return (now - dismissedTime) < DISMISSAL_DURATION
})

// Should show modal: user has no plan AND hasn't dismissed recently AND not permanently dismissed
const shouldShowModal = computed(() => {
  return !hasActivePlan.value && !isDismissed.value && !isPermanentlyDismissed.value && !isLoadingSubscription.value
})

// Fetch subscription status using the comprehensive plan data
async function fetchSubscriptionStatus() {
  if (!props.session) {
    return
  }

  try {
    isLoadingSubscription.value = true
    // Use the more comprehensive getCurrentUserPlans instead of getSubscriptionStatus
    const planData = await trpc.stripe.getCurrentUserPlans.query()
    // Convert to compatible format for existing logic
    subscriptionData.value = {
      isSubscribed: planData.hasActiveSubscription,
      status: planData.currentPlan?.status || null,
      currentPeriodEnd: planData.currentPlan?.currentPeriodEnd || null,
      cancelAtPeriodEnd: planData.currentPlan?.cancelAtPeriodEnd || false,
      planType: planData.currentPlan?.planType || null,
    }
  }
  catch (error) {
    console.error('Failed to fetch subscription status:', error)
    // Fallback to basic subscription check if detailed plan fetch fails
    try {
      subscriptionData.value = await trpc.stripe.getSubscriptionStatus.query({ verifyWithStripe: false })
    }
    catch (fallbackError) {
      console.error('Fallback subscription check failed:', fallbackError)
    }
  }
  finally {
    isLoadingSubscription.value = false
  }
}

// Initialize subscription check and modal visibility
onMounted(async () => {
  // Check if user just completed a successful purchase
  if (route.query.billing_success === 'true') {
    // Permanently dismiss the modal since they now have a subscription
    localStorage.setItem(PERMANENT_DISMISSAL_KEY, 'true')
    return // Don't show the modal
  }

  await fetchSubscriptionStatus()

  if (shouldShowModal.value) {
    isOpen.value = true
  }
})

function handleClose() {
  isOpen.value = false
  emit('close')
}

function handleDecideLater() {
  // Remember dismissal for 2 days
  localStorage.setItem(DISMISSAL_KEY, Date.now().toString())
  handleClose()
}

// Watch for changes in shouldShowModal to handle reactive updates
watch(shouldShowModal, (newValue) => {
  if (newValue && !isOpen.value) {
    isOpen.value = true
  }
})
</script>

<template>
  <Dialog v-model:open="isOpen" @update:open="!$event && handleClose()">
    <DialogContent class="max-w-6xl max-h-[90vh] overflow-y-auto">
      <DialogHeader>
        <DialogTitle class="text-2xl font-bold text-center">
          Choisissez votre plan
        </DialogTitle>

        <DialogDescription class="text-center text-lg mt-2">
          Tous les plans Selfwise donnent accès à toutes les fonctionalités.
        </DialogDescription>
      </DialogHeader>

      <div class="mt-8">
        <PricingCards
          :user-logged-in="!!session"
          :session="session"
          cycle="monthly"
        />
      </div>

      <div class="flex justify-center mt-8 pt-6 border-t">
        <Button
          variant="ghost"
          class="text-muted-foreground hover:text-foreground"
          @click="handleDecideLater"
        >
          Je déciderai plus tard
        </Button>
      </div>
    </DialogContent>
  </Dialog>
</template>

<style scoped>
/* Ensure the dialog content scrolls properly on smaller screens */
:deep(.max-h-\[90vh\]) {
  max-height: 90vh;
}
</style>
